/**
 * The pairs the dedup engine could not settle on its own, for a person to.
 *
 * Automatic merges happen only on exact evidence — one name, one company
 * and one shared address (folded field-by-field, so the survivor gains what
 * the duplicate held and loses nothing); a shared address; an identical name
 * against one addressed row. Everything softer (same surname
 * at one firm, initials that fit a name) lands here as a candidate with its
 * reason and confidence, and a person says which record survives. "Not the
 * same" is a person's decision and the server remembers it: the pair is never
 * offered again and the automatic pass never folds it. Kept only in React
 * state, it came back on every reload (verification C13).
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { GitMerge, Loader2, X } from 'lucide-react'

import {
  crmErrorDetail,
  crmService,
  recordKindLabel,
  type CrmDuplicateCandidate,
  type CrmDuplicateRecord,
} from '@/services/crm'
import { survivorsFor } from './crmFields'

const pairKey = (c: CrmDuplicateCandidate) => c.ids.join('|')

const recordFor = (c: CrmDuplicateCandidate, id: string): CrmDuplicateRecord | undefined =>
  c.records?.find((r) => r.id === id)


const DASH = '\u2014'

function shortLabel(r: CrmDuplicateRecord | undefined, fallback: string): string {
  // What tells this row apart at a glance, in the order a desk would read it:
  // the address, then the firm, then the id.
  return r?.email || r?.company || fallback
}

function seenOn(iso: string | null | undefined): string {
  if (!iso) return DASH
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? DASH : d.toLocaleDateString()
}

/** Side-by-side of the fields the two rows do not agree on.
 *
 *  Without this the card showed one name twice and two buttons reading
 *  "Keep <same name>" — a choice with nothing to choose on. A merge fills the
 *  survivor's empty fields from the other row, so a value only one side holds
 *  is marked as carried over, not as a conflict; the conflicts are the only
 *  places the survivor choice changes what is stored. */
function DifferenceTable({ c }: { c: CrmDuplicateCandidate }) {
  const rows = c.differing ?? []
  const [left, right] = c.records ?? [undefined, undefined]
  if (rows.length === 0) {
    return (
      <div className="mt-1.5 text-[10px] text-stone-500">
        Every displayed field matches. Merge combines both records into one contact.
      </div>
    )
  }
  return (
    <div className="mt-1.5 overflow-x-auto rounded border border-white/[0.06]">
      <table className="w-full min-w-[420px] border-collapse text-[10px]">
        <thead>
          <tr className="text-left text-stone-500">
            <th className="px-2 py-1 font-normal">Field</th>
            <th className="px-2 py-1 font-normal text-stone-300">{shortLabel(left, c.names[0])}</th>
            <th className="px-2 py-1 font-normal text-stone-300">{shortLabel(right, c.names[1])}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.field} className="border-t border-white/[0.04] align-top">
              <td className="px-2 py-1 text-stone-500">{d.label}</td>
              {d.values.map((v, i) => (
                <td
                  key={i}
                  className={clsx(
                    'px-2 py-1 break-all',
                    v ? (d.conflict ? 'text-amber-200' : 'text-emerald-300') : 'text-stone-600',
                  )}
                  title={d.conflict ? 'The two rows disagree here' : 'Only one row holds this — the merge keeps it'}
                >
                  {v || DASH}
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-white/[0.04] text-stone-500">
            <td className="px-2 py-1">Last seen</td>
            <td className="px-2 py-1">{seenOn(left?.lastSeenAt)}</td>
            <td className="px-2 py-1">{seenOn(right?.lastSeenAt)}</td>
          </tr>
          <tr className="border-t border-white/[0.04] text-stone-500">
            <td className="px-2 py-1">Captures / deals</td>
            <td className="px-2 py-1">{left ? `${left.captureCount} / ${left.dealCount}` : DASH}</td>
            <td className="px-2 py-1">{right ? `${right.captureCount} / ${right.dealCount}` : DASH}</td>
          </tr>
        </tbody>
      </table>
      <div className="border-t border-white/[0.04] px-2 py-1 text-[9px] text-stone-600">
        <span className="text-amber-200">Amber</span> = different values; both remain on the combined profile.{' '}
        <span className="text-emerald-300">Green</span> = only one row has it; the merge keeps it either way.
      </div>
    </div>
  )
}

export default function DuplicateReview({ onOpenContact }: { onOpenContact: (id: string) => void }) {
  const queryClient = useQueryClient()
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set())
  const [choosing, setChoosing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [primary, setPrimary] = useState<Record<string, string>>({})

  const duplicates = useQuery({ queryKey: ['crm', 'duplicates'], queryFn: () => crmService.getDuplicates() })

  const merge = useMutation({
    mutationFn: ({ fold, keep }: { fold: string; keep: string }) => crmService.mergeContact(fold, keep),
    onSuccess: () => {
      setChoosing(null)
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'lists'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'duplicates'] })
    },
    onError: (err) => setError(crmErrorDetail(err, 'The contacts could not be merged.')),
  })

  const notSame = useMutation({
    mutationFn: (ids: [string, string]) => crmService.markNotSame(ids),
    onMutate: (ids) => {
      setError(null)
      // Hidden at once; brought back if the server does not keep the answer.
      setDismissed((prev) => new Set(prev).add(ids.join('|')))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm', 'duplicates'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] })
    },
    onError: (err, ids) => {
      setDismissed((prev) => {
        const next = new Set(prev)
        next.delete(ids.join('|'))
        return next
      })
      setError(crmErrorDetail(err, '“Not the same” could not be saved; the pair is still listed.'))
    },
  })

  const candidates = (duplicates.data?.candidates ?? []).filter((c) => !dismissed.has(pairKey(c)))

  return (
    <div className="panel flex min-h-0 flex-col">
      <div className="panel-header">
        <span className="panel-header-title">Duplicate review</span>
        <span className="font-mono text-[9px] text-stone-500">
          {duplicates.data ? `${candidates.length} pair${candidates.length === 1 ? '' : 's'}` : ''}
        </span>
      </div>
      <div className="panel-body min-h-0 flex-1 space-y-2 overflow-y-auto scrollbar-thin">
        {duplicates.isPending ? (
          <div className="flex items-center gap-1.5 text-[10px] text-stone-500">
            <Loader2 size={10} className="motion-safe:animate-spin" /> Looking for pairs…
          </div>
        ) : duplicates.isError ? (
          <div className="text-[10px] text-red-300">{crmErrorDetail(duplicates.error, 'The candidates could not be read.')}</div>
        ) : candidates.length === 0 ? (
          <div className="text-[11px] italic text-stone-500">
            No pairs are waiting. A contact repeated under the same name, company and address is merged on arrival \u2014 fields united, nothing lost. Anything softer would be listed here.
          </div>
        ) : (
          candidates.map((c) => {
            const key = pairKey(c)
            const options = survivorsFor(c)
            const open = choosing === key
            const recommended = [...options].sort((a, b) =>
              (recordFor(c, b.keep)?.captureCount ?? 0) - (recordFor(c, a.keep)?.captureCount ?? 0))[0]
            const selected = options.find((o) => o.keep === primary[key]) ?? recommended
            return (
              <div key={key} className="rounded border border-amber-400/10 bg-black/20 px-3 py-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-1.5 text-[11px] text-stone-200">
                      {c.ids.map((id, i) => (
                        <span key={id} className="inline-flex items-center gap-1.5">
                          {i > 0 ? <span className="text-stone-600">↔</span> : null}
                          <button
                            type="button"
                            onClick={() => onOpenContact(id)}
                            className="rounded underline decoration-dotted decoration-stone-600 underline-offset-2 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
                          >
                            {c.names[i] || id}
                          </button>
                          <span className="font-mono text-[8px] uppercase tracking-wider text-stone-600">{recordKindLabel(id)}</span>
                        </span>
                      ))}
                    </div>
                    <div className="mt-0.5 text-[10px] text-stone-500">{c.reason}</div>
                    <DifferenceTable c={c} />
                  </div>
                  <span
                    className={clsx(
                      'shrink-0 rounded border px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider',
                      c.confidence === 'high' ? 'border-amber-400/30 text-amber-200' : 'border-stone-700 text-stone-400',
                    )}
                  >
                    {c.confidence}
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {open ? (
                    <div className="w-full space-y-2">
                      <p className="text-[10px] text-stone-300">Combine both records into one contact. Email addresses, notes, deal links and alternate details are retained. Different values remain available under Also observed.</p>
                      {options.length > 1 && <label className="flex flex-wrap items-center gap-2 text-[10px] text-stone-400">
                        Primary profile
                        <select aria-label="Primary profile" value={selected?.keep}
                          onChange={(event) => setPrimary((current) => ({ ...current, [key]: event.target.value }))}
                          className="min-w-0 max-w-full rounded border border-amber-400/20 bg-black px-2 py-1 text-stone-200">
                          {options.map((o) => <option key={o.keep} value={o.keep}>{recordFor(c, o.keep)?.email || o.keepName || o.keep}</option>)}
                        </select>
                        <span className="text-stone-500">Sets the main display; both records are combined.</span>
                      </label>}
                      <button type="button" disabled={merge.isPending || !selected}
                        onClick={() => selected && merge.mutate({ fold: selected.fold, keep: selected.keep })}
                        className="inline-flex items-center gap-1 rounded-md border border-amber-400/30 bg-amber-400/[0.12] px-2 py-1 text-[10px] text-amber-200 hover:bg-amber-400/[0.2] disabled:opacity-50">
                        {merge.isPending ? <Loader2 size={9} className="motion-safe:animate-spin" /> : <GitMerge size={9} />}
                        Merge contacts
                      </button>
                    </div>
                  ) : options.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setChoosing(key)}
                      className="inline-flex items-center gap-1 rounded-md border border-amber-400/15 px-2 py-0.5 text-[10px] text-stone-300 hover:border-amber-400/30 hover:text-stone-100 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
                    >
                      <GitMerge size={9} /> Merge →
                    </button>
                  ) : (
                    <span className="text-[9px] text-stone-600">Neither is a captured contact; merge masters from the broker directory.</span>
                  )}
                  <button
                    type="button"
                    disabled={!open && notSame.isPending}
                    onClick={() => {
                      // Backing out of the survivor choice is not a judgement
                      // that the two are different people.
                      if (open) setChoosing(null)
                      else notSame.mutate(c.ids)
                    }}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[10px] text-stone-500 hover:text-stone-200 disabled:opacity-50 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
                  >
                    <X size={9} /> {open ? 'Cancel' : 'Not the same'}
                  </button>
                </div>
              </div>
            )
          })
        )}
        {error ? <div className="text-[10px] text-red-300">{error}</div> : null}
      </div>
    </div>
  )
}
